import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";

import { fetchParticipantById } from "@/lib/api/participants";
import type { PitchAnalysis } from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ParticipantShell } from "@/components/layout/ParticipantShell";

// V2 spec frame P4. Three qualitative blocks shown right after the
// upload + Whisper + GPT pipeline lands the result on the participant
// row. We poll until pitch_analysis is populated (~5-10s typical).
export function PitchAnalysisPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = slug ? getParticipantSession(slug) : null;

  // Track wall-clock time on the page so we keep showing the waiting
  // state across the 3-second gaps between poll fetches. Without this,
  // `isFetching` flickers false in those windows and we'd briefly
  // render the "Analysis didn't run" copy even though the pipeline is
  // still grinding in the background.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const t = window.setInterval(() => {
      setElapsedMs(performance.now() - start);
    }, 500);
    return () => window.clearInterval(t);
  }, []);
  const ANALYSIS_TIMEOUT_MS = 120_000; // 2 minutes — generous; covers
  // slow Whisper + GPT runs on long pitches.

  const query = useQuery({
    queryKey: ["pitch-analysis", session?.participantId],
    queryFn: () => fetchParticipantById(session?.participantId ?? ""),
    enabled: Boolean(session?.participantId),
    refetchInterval: (q) => (q.state.data?.pitch_analysis ? false : 3000),
    retry: false,
  });

  const analysis = query.data?.pitch_analysis ?? null;
  // Waiting state holds for the whole window — between polls, while
  // the first fetch is still in flight, and across the gaps where
  // useQuery has technically settled but the server hasn't written
  // pitch_analysis yet. Falls into NoAnalysisState only once the
  // timeout elapses with the column still null.
  const isWaiting = !analysis && elapsedMs < ANALYSIS_TIMEOUT_MS;

  if (!session) {
    if (slug) navigate(`/e/${slug}`, { replace: true });
    return null;
  }

  return (
    <ParticipantShell>
      <div className="mx-auto w-full max-w-[480px] px-4 py-6">
        {isWaiting ? (
          <WaitingState elapsedMs={elapsedMs} />
        ) : analysis ? (
          <AnalysisResult analysis={analysis} />
        ) : (
          <NoAnalysisState />
        )}

        <div className="mt-6 flex flex-col gap-2">
          <Button
            type="button"
            fullWidth
            onClick={() => navigate(`/e/${slug}/feed`)}
          >
            Looks good
          </Button>
          <LinkButton to={`/e/${slug}/pitch`} variant="secondary" fullWidth>
            Re-record
          </LinkButton>
        </div>
      </div>
    </ParticipantShell>
  );
}

function WaitingState({ elapsedMs }: { elapsedMs: number }) {
  const elapsedSec = Math.floor(elapsedMs / 1000);
  // Rotating phases that mirror what the server is actually doing —
  // transcribe → portrait → positioning → strengthen. Each phase
  // lasts ~6s; we drift to the last one and hold there for slow
  // runs so the user never sees "still on step 1" past 30 seconds.
  const PHASES: { from: number; label: string }[] = [
    { from: 0, label: "Listening to your pitch…" },
    { from: 6, label: "Reading the transcript…" },
    { from: 12, label: "Tracing how you come across…" },
    { from: 20, label: "Lining it up with your goals…" },
    { from: 28, label: "Pulling together the read…" },
    { from: 40, label: "Almost there — sharpening the wording…" },
  ];
  const phase = PHASES.slice().reverse().find((p) => elapsedSec >= p.from)
    ?? PHASES[0];

  // Visual progress bar — caps at 95% so it never reads "done" while
  // the response hasn't landed. Eased so most of the bar moves in
  // the first half (when the API typically responds).
  const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-elapsedSec / 20))));

  return (
    <Card padding="lg" className="text-center">
      <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#2563eb]" strokeWidth={2} />
      <h1 className="mt-4 text-[20px] font-bold tracking-tight text-[#1a1a1a]">
        Reading your pitch
      </h1>
      <p className="mt-2 min-h-[40px] text-[13.5px] leading-relaxed text-[#6c757d]">
        {phase.label}
      </p>

      <div className="mx-auto mt-4 h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-[#e2e8f0]">
        <div
          className="h-full rounded-full bg-[#2563eb] transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-[11px] tabular-nums text-[#94a3b8]">
        {elapsedSec}s
      </p>
      <p className="mt-4 text-[12px] leading-relaxed text-[#94a3b8]">
        Usually 10–20 seconds. Long pitches and slow connections can take a bit more — you can
        leave the page open or come back later.
      </p>
    </Card>
  );
}

function NoAnalysisState() {
  return (
    <Card padding="lg" className="text-center">
      <h1 className="text-[20px] font-bold tracking-tight text-[#1a1a1a]">
        Analysis didn't run
      </h1>
      <p className="mt-2 text-[13px] text-[#6c757d]">
        Your pitch is recorded, but the read couldn't be generated. Re-recording usually sorts
        this out.
      </p>
    </Card>
  );
}

function AnalysisResult({ analysis }: { analysis: PitchAnalysis }) {
  const hasPortrait = analysis.portrait.trim().length > 0;
  const hasPositioning =
    analysis.positioning.trim().length > 0 && analysis.positioning !== "—";
  const hasStrengthen = analysis.strengthen.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="px-1">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.6px] text-[#94a3b8]">
          <Sparkles className="h-3.5 w-3.5" />
          The reward
        </div>
        <h1 className="mt-2 text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1a1a1a]">
          Looks great — here's what comes across
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-[#6c757d]">
          We compared your pitch with your goals.
        </p>
      </div>

      {/* Block 1: portrait — soft "who you read as", no Adizes terms. */}
      {hasPortrait ? (
        <div className="rounded-[14px] border border-[#cfe0fb] bg-[#f3f7ff] p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#1e40af]">
            The kind of person you read as
          </p>
          <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-[#1b3a6b]">
            {analysis.portrait}
          </p>
        </div>
      ) : null}

      {/* Block 2: positioning — pitch vs declared goal. */}
      {hasPositioning ? (
        <div className="rounded-[14px] border border-[#f3e0a6] bg-[#fef9e9] p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#b45309]">
            How it lands on your goal
          </p>
          <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-[#7a5b12]">
            {analysis.positioning}
          </p>
        </div>
      ) : null}

      {/* Block 3: strengthen — 2 concrete tips, numbered. */}
      {hasStrengthen ? (
        <div className="rounded-[14px] border border-[#e2e8f0] bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#94a3b8]">
            What to strengthen
          </p>
          <ol className="mt-2 flex flex-col gap-2">
            {analysis.strengthen.map((tip, i) => (
              <li key={i} className="flex gap-2 text-[13.5px] leading-relaxed text-[#1a1a1a]">
                <span className="font-bold text-[#2563eb]">{i + 1}</span>
                <span>{tip}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <p className="mt-1 px-1 text-[11px] text-[#94a3b8]">
        Scope: how this single pitch reads. Not a personality verdict, not career advice.
      </p>
    </div>
  );
}
